import React, { useCallback, useEffect, useState } from "react";
import { FileText, CheckCircle2, RefreshCw, Mail, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const AdminCoaPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("open");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/coa-requests", { headers, params: { status } });
            setItems(data);
        } catch {
            toast.error("Failed to load COA requests");
        }
        setLoading(false);
        // eslint-disable-next-line
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (id) => {
        try {
            await api.post(`/admin/coa-requests/${id}/resolve`, {}, { headers });
            toast.success("Marked resolved");
            load();
        } catch {
            toast.error("Could not update");
        }
    };

    return (
        <div data-testid="admin-coa-panel">
            <div className="flex items-center gap-2 mb-5 flex-wrap">
                {["open", "resolved", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatus(s)}
                        className={`px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border ${
                            status === s ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]" : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                        }`}
                        data-testid={`admin-coa-tab-${s}`}
                    >
                        {s}
                    </button>
                ))}
                <button onClick={load} className="ml-auto btn-neon" data-testid="admin-coa-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading…</div>
            ) : items.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-coa-empty">
                    <FileText size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                    No requests in this view.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((r) => (
                        <div
                            key={r.id}
                            className="glass-elevated rounded-2xl p-4 flex items-start gap-4 flex-wrap"
                            data-testid={`admin-coa-row-${r.id}`}
                        >
                            <div className="flex-1 min-w-[220px]">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-display font-bold text-white">{r.product_name}</span>
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{r.product_slug}</span>
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                                        r.status === "resolved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-amber-500/40 bg-amber-500/10 text-amber-200"
                                    }`}>
                                        {r.status}
                                    </span>
                                </div>
                                <div className="text-xs text-[var(--text-secondary)]">
                                    {r.customer_name || "Anonymous"} ·{" "}
                                    <a href={`mailto:${r.customer_email}`} className="text-[var(--gold)] hover:underline">{r.customer_email}</a>
                                </div>
                                {r.note && (
                                    <div className="mt-2 text-xs text-[var(--text-secondary)] flex items-start gap-2 italic">
                                        <MessageSquareQuote size={12} className="text-[var(--gold)] mt-0.5" />
                                        <span>{r.note}</span>
                                    </div>
                                )}
                                <div className="text-[10px] text-[var(--text-muted)] mt-1 font-mono">
                                    {new Date(r.created_at).toLocaleString("en-AU")}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <a
                                    href={`mailto:${r.customer_email}?subject=Carbon Labs%20%E2%80%94%20Manufacturer-supplied%20COA%20for%20${encodeURIComponent(r.product_name)}&body=Hi%20${encodeURIComponent(r.customer_name || "")}%2C%0A%0APlease%20find%20attached%20the%20manufacturer-supplied%20Certificate%20of%20Analysis%20for%20${encodeURIComponent(r.product_name)}.%0A%0ACarbon%20Labs%20team`}
                                    className="px-3 py-1.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                    data-testid={`admin-coa-mail-${r.id}`}
                                >
                                    <Mail size={11} /> Email
                                </a>
                                {r.status !== "resolved" && (
                                    <button
                                        onClick={() => resolve(r.id)}
                                        className="px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                        data-testid={`admin-coa-resolve-${r.id}`}
                                    >
                                        <CheckCircle2 size={11} /> Mark sent
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
